import { ApiError } from '../../lib/api';

export type FetchType = 'http' | 'headless' | 'pdf';
export type EditionType = 'alerta' | 'boletin';
export type EditionStatus = 'draft' | 'in_review' | 'approved' | 'sent';
export type Prioridad = 'ALTA' | 'MEDIA' | 'BAJA';

/** Fuente de datos regulatorios (7 fuentes configurables). */
export interface Source {
  id: string;
  code: string;
  name: string;
  url: string;
  section: string | null;
  fetch_type: FetchType;
  country: string | null;
  llm_brief: string | null;
  filter_config: unknown;
  enabled: boolean;
  last_fetched_at: string | null;
}

/** Edición tal como aparece en el listado. */
export interface EditionListItem {
  id: string;
  type: EditionType;
  edition_number: number;
  status: EditionStatus;
  period_start: string | null;
  period_end: string | null;
  title: string | null;
  created_at: string;
  approved_at: string | null;
  sent_at: string | null;
  rejected_reason: string | null;
  /** Autoría en el panel compartido: nombre de quien la generó; null = corrida programada. */
  generada_por?: string | null;
}

export interface Classification {
  priority: Prioridad;
  score: number;
  requires_human: boolean;
  status: string;
  justification: string;
  sectors: string[];
}

export interface ItemRef {
  title: string;
  url: string;
  organism: string | null;
  doc_type: string | null;
  norm_identifier: string | null;
  needs_review: boolean;
  classifications: Classification[];
}

export interface PlazoClave {
  fecha: string;
  hito: string;
}

/** Resumen editorial producido por el agente N3. */
export interface Summary {
  titular: string;
  que_paso: string;
  a_quien_afecta: string;
  implicancia_practica: string;
  plazos_clave: PlazoClave[];
  contexto: string;
  url_fuente: string;
}

export interface VerificacionFactual {
  afirmacion: string;
  estado: string;
  evidencia: string;
}

/** Veredicto de control de calidad producido por el agente N4. */
export interface QaVerdict {
  resultado: 'APROBADO' | 'RECHAZADO';
  verificacion_factual: VerificacionFactual[];
  omisiones_criticas: unknown;
  instrucciones_reparacion: unknown;
  mejoras_opcionales: unknown;
}

export interface EditionItem {
  id: string;
  item_id: string;
  summary: Summary | null;
  qa_verdict: QaVerdict | null;
  section: string | null;
  position: number;
  /** Corrida manual: edición que ya publicó este item (sello "Ya publicado"). */
  republicado_de?: string | null;
  /** Rescatada por el revisor: motivo original del descarte de la editora (o 'baja'). */
  incorporado_motivo?: string | null;
  items: ItemRef;
}

export interface EditionDetail extends EditionListItem {
  editorial: string | null;
  html: string | null;
  edition_items: EditionItem[];
  /** BAJA de la alerta: lista compacta "Otros movimientos del día" (solo titular + link). */
  otros_movimientos?: { titulo: string; url: string | null; organismo: string | null }[];
}

/** Configuración global del newsletter (§ Ajustes). */
export interface NewsletterSettings {
  approver: { email: string; name: string };
  sender: { from_email: string; from_name: string };
  feature_segmentation_enabled: boolean;
  feature_minisite_enabled: boolean;
  auto_send: boolean;
  approval_token_ttl_hours: number;
  /** Correos que reciben los envíos mientras modo_prueba está activo. */
  test_recipients?: string[];
  /** Envíos programados van SOLO a los destinatarios de prueba. */
  modo_prueba?: boolean;
}

/** Programación de Víctor (tabla schedules). */
export interface Programacion {
  id: string;
  descripcion: string;
  tipo: EditionType;
  interpretacion: string;
  enabled: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string;
}

export interface NewsletterJob {
  id: string;
  status: string;
  error?: string | null;
}

export function traducirError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'Ocurrió un error inesperado. Inténtalo de nuevo.';
}

/** Normaliza campos que pueden venir como string o string[] a una lista. */
export function comoLista(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x)).filter((s) => s.trim() !== '');
  if (typeof v === 'string' && v.trim() !== '') return [v];
  return [];
}

export const FETCH_TYPES: FetchType[] = ['http', 'headless', 'pdf'];

export const FETCH_TYPE_LABEL: Record<FetchType, string> = {
  http: 'HTTP',
  headless: 'Headless',
  pdf: 'PDF',
};

export const EDITION_TYPE_LABEL: Record<EditionType, string> = {
  alerta: 'Alerta',
  boletin: 'Boletín',
};

export const STATUS_LABEL: Record<EditionStatus, string> = {
  draft: 'Borrador',
  in_review: 'En revisión',
  approved: 'Aprobada',
  sent: 'Enviada',
};

export const STATUS_CLASE: Record<EditionStatus, string> = {
  draft: 'bg-slate-100 text-slate-600 ring-slate-200',
  in_review: 'bg-amber-50 text-amber-700 ring-amber-200',
  approved: 'bg-celeste/30 text-azul-profundo ring-celeste',
  sent: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
};

export const PRIORIDAD_CLASE: Record<Prioridad, string> = {
  ALTA: 'bg-red-50 text-red-700 ring-red-200',
  MEDIA: 'bg-amber-50 text-amber-700 ring-amber-200',
  BAJA: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export function fmtFecha(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Las fechas de solo-día ('2026-07-10') se parsean como medianoche UTC; renderizarlas en
  // hora chilena (UTC-4) las retrocede un día. Se fuerzan a UTC para que el día no cambie.
  const soloDia = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  return d.toLocaleDateString('es-CL', {
    day: '2-digit', month: 'short', year: 'numeric', ...(soloDia ? { timeZone: 'UTC' } : {}),
  });
}

export const inputClass =
  'mt-2 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-[15px] font-normal text-texto outline-none transition-colors duration-150 placeholder:text-gris/60 focus:border-azul-profundo focus:ring-2 focus:ring-azul-profundo/25 disabled:bg-slate-50 disabled:text-gris';

export const labelClass = 'block text-xs font-medium uppercase tracking-[0.18em] text-azul-profundo';
